import { jwtDecode } from 'jwt-decode';
import type { JWTPayload, AppUser } from '../types';

export class JWTError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'JWTError';
  }
}

export const getTokenFromURL = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log('🔍 Checking URL for token:', {
      fullURL: window.location.href,
      searchParams: window.location.search,
      tokenFound: !!token,
      tokenLength: token?.length || 0
    });
  }
  
  return token;
};

export const validateToken = (token: string): boolean => {
  if (!token) return false;
  
  try {
    const payload = jwtDecode<JWTPayload>(token);
    
    // Check if token has expired
    const currentTime = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < currentTime) {
      throw new JWTError('Token has expired', 'TOKEN_EXPIRED');
    }
    
    // Validate required fields
    if (!payload.sub || !payload.active_flat_id || !payload.user_type) {
      throw new JWTError('Token missing required fields', 'INVALID_TOKEN');
    }
    
    return true;
  } catch (error) {
    if (error instanceof JWTError) {
      throw error;
    }
    throw new JWTError('Invalid token format', 'MALFORMED_TOKEN');
  }
};

export const decodeToken = (token: string): JWTPayload => {
  try {
    return jwtDecode<JWTPayload>(token);
  } catch (error) {
    throw new JWTError('Failed to decode token', 'DECODE_ERROR');
  }
};

export const extractUserFromToken = (token: string): AppUser => {
  const payload = decodeToken(token);
  
  return {
    id: payload.sub,
    userType: payload.user_type,
    activeFlatId: payload.active_flat_id,
    permissions: payload.permissions || []
  };
};

export const isTokenExpiringSoon = (token: string, thresholdMinutes: number = 5): boolean => {
  try {
    const payload = decodeToken(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const thresholdTime = currentTime + (thresholdMinutes * 60);
    
    return payload.exp ? payload.exp < thresholdTime : false;
  } catch {
    return true;
  }
};

export const getTokenExpiryTime = (token: string): Date | null => {
  try {
    const payload = decodeToken(token);
    return payload.exp ? new Date(payload.exp * 1000) : null;
  } catch {
    return null;
  }
};

export const clearTokenFromURL = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  window.history.replaceState({}, document.title, url.toString());
};